// components/home/HeroSection.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';

const HeroSection = () => {
    const navigate = useNavigate();
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);

    // Hero images with proper clinic-specific content
    const heroImages = [
        {
            src: "/assets/doctor-patient-consultation.jpg",
            alt: "Doctor consulting with patient",
            description: "Personalized care with experienced physicians",
            title: "Expert Medical Consultation",
            badge: "👨‍⚕️ Expert Doctors"
        },
        {
            src: "/assets/modern-clinic-interior.jpg",
            alt: "Modern clinic interior",
            description: "State-of-the-art facilities for your comfort",
            title: "Modern Healthcare Facility",
            badge: "🏥 Advanced Equipment"
        },
        {
            src: "/assets/emergency-care.jpg",
            alt: "Emergency medical care",
            description: "24/7 emergency services available",
            title: "Emergency Care Always Available",
            badge: "🚑 24/7 Emergency"
        },
        {
            src: "/assets/family-care.jpg",
            alt: "Family healthcare",
            description: "Comprehensive care for the whole family",
            title: "Family-Centered Healthcare",
            badge: "👨‍👩‍👧‍👦 Family Medicine"
        },
        {
            src: "/assets/laboratory-services.jpg",
            alt: "Laboratory testing",
            description: "Accurate diagnostic laboratory services",
            title: "Advanced Diagnostic Services",
            badge: "🔬 Lab Tests"
        },
        {
            src: "/assets/maternal-care.jpg",
            alt: "Maternal and child care",
            description: "Specialized care for mothers and children",
            title: "Maternal & Child Healthcare",
            badge: "🤱 Maternal Care"
        }
    ];

    // Simple image preloading without Promise rejection
    useEffect(() => {
        heroImages.forEach(img => {
            const image = new Image();
            image.src = img.src;
        });
    }, []);

    // Auto-rotate images
    useEffect(() => {
        let interval;
        if (isAutoPlaying) {
            interval = setInterval(() => {
                setCurrentImageIndex((prevIndex) => 
                    prevIndex === heroImages.length - 1 ? 0 : prevIndex + 1
                );
            }, 5000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isAutoPlaying, heroImages.length]);

    const goToNext = () => {
        setCurrentImageIndex((prevIndex) => 
            prevIndex === heroImages.length - 1 ? 0 : prevIndex + 1
        );
    };

    const goToPrevious = () => {
        setCurrentImageIndex((prevIndex) => 
            prevIndex === 0 ? heroImages.length - 1 : prevIndex - 1
        );
    };

    const goToImage = (index) => {
        setCurrentImageIndex(index);
    };

    const toggleAutoPlay = () => {
        setIsAutoPlaying(!isAutoPlaying);
    };

    // Fallback image if main image fails to load
    const handleImageError = (e) => {
        console.log('Image failed to load:', e.target.src);
        // You could set a fallback image here
        // e.target.src = '/assets/fallback-image.jpg';
    };

    // Handle button clicks safely
    const handleBookAppointment = (e) => {
        e.preventDefault();
        // Add your appointment booking logic here
        console.log('Book appointment clicked');
        // navigate('/appointment');
    };

    const handleStaffLogin = (e) => {
        e.preventDefault();
        navigate('/');
    };

    return (
        <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-blue-50 via-white to-cyan-50">
            {/* Simplified Background - remove animated blobs if causing issues */}
            <div className="absolute inset-0 opacity-10 bg-gradient-to-br from-blue-100 to-cyan-100"></div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-24 lg:py-32 relative z-10">
                <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                    {/* Left Column - Text Content */}
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center lg:text-left order-2 lg:order-1"
                    >
                        {/* Clinic Badge */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-blue-100 to-cyan-100 border border-blue-200 text-blue-700 font-medium mb-6 shadow-sm"
                        >
                            <span className="w-2 h-2 bg-blue-600 rounded-full mr-2 animate-pulse"></span>
                            Trusted Healthcare Since 2005
                        </motion.div>

                        {/* Main Heading */}
                        <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 lg:mb-6 leading-tight">
                            <span className="text-blue-600">Dr. Asrat</span> Medical Clinic
                            <span className="block text-gray-800 text-2xl md:text-3xl lg:text-4xl mt-2">
                                Your Health, Our Commitment
                            </span>
                        </h1>

                        {/* Subheading */}
                        <p className="text-base md:text-lg text-gray-600 mb-6 lg:mb-8 max-w-2xl">
                            Providing comprehensive medical care with compassion, 
                            modern technology, and experienced healthcare professionals 
                            dedicated to your well-being.
                        </p>

                        {/* Key Services */}
                        <div className="flex flex-wrap gap-2 mb-6 lg:mb-8 justify-center lg:justify-start">
                            {[
                                "Surgery",
                                "Family Planning",
                                "Maternal Care",
                                "Laboratory",
                                "Pharmacy",
                                "Emergency",
                                "Medical Certificates",
                                "Chronic Care"
                            ].map((service, index) => (
                                <span 
                                    key={index}
                                    className="px-3 py-1.5 bg-white border border-blue-100 rounded-full text-xs font-medium text-blue-700 shadow-sm hover:shadow-md transition-shadow"
                                >
                                    {service}
                                </span>
                            ))}
                        </div>

                        {/* CTA Buttons */}
                        <div className="flex flex-col sm:flex-row gap-3 mb-8 lg:mb-10">
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={handleBookAppointment}
                                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Book Appointment
                            </motion.button>
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={handleStaffLogin}
                                className="px-6 py-3 bg-white text-blue-600 font-semibold rounded-lg border-2 border-blue-600 hover:bg-blue-50 transition-colors duration-300 shadow-sm flex items-center justify-center gap-2"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                                </svg>
                                Staff Login
                            </motion.button>
                        </div>

                        {/* Quick Stats */}
                        <div className="grid grid-cols-3 gap-3 lg:gap-4">
                            {[
                                { value: "18+", label: "Years", icon: "📅" },
                                { value: "50K+", label: "Patients", icon: "👥" },
                                { value: "95%", label: "Satisfied", icon: "⭐" }
                            ].map((stat, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.3 + index * 0.1 }}
                                    className="text-center p-3 bg-white/70 backdrop-blur-sm rounded-xl border border-white/20 shadow-sm"
                                >
                                    <div className="text-xl md:text-2xl font-bold text-blue-700 mb-1">{stat.value}</div>
                                    <div className="text-xs text-gray-600">{stat.label}</div>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>

                    {/* Right Column - Image Carousel */}
                    <div className="relative order-1 lg:order-2">
                        {/* Image Carousel Container */}
                        <div className="relative h-[300px] md:h-[400px] lg:h-[500px] rounded-2xl overflow-hidden shadow-xl">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={currentImageIndex}
                                    initial={{ opacity: 0, scale: 1.1 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    transition={{ duration: 0.5 }}
                                    className="absolute inset-0"
                                >
                                    <img 
                                        src={heroImages[currentImageIndex].src}
                                        alt={heroImages[currentImageIndex].alt}
                                        className="w-full h-full object-cover"
                                        onError={handleImageError}
                                    />
                                    
                                    {/* Image Overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                                    
                                    {/* Image Badge */}
                                    <motion.div
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.3 }}
                                        className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-lg"
                                    >
                                        <div className="flex items-center gap-1">
                                            <span className="text-sm">{heroImages[currentImageIndex].badge.split(' ')[0]}</span>
                                            <span className="text-xs font-medium text-gray-800">
                                                {heroImages[currentImageIndex].badge.split(' ').slice(1).join(' ')}
                                            </span>
                                        </div>
                                    </motion.div>
                                    
                                    {/* Image Title & Description */}
                                    <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                                        <motion.h3
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.2 }}
                                            className="text-lg md:text-xl font-bold text-white mb-1"
                                        >
                                            {heroImages[currentImageIndex].title}
                                        </motion.h3>
                                        <motion.p
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ delay: 0.4 }}
                                            className="text-white/90 text-xs md:text-sm"
                                        >
                                            {heroImages[currentImageIndex].description}
                                        </motion.p>
                                    </div>
                                </motion.div>
                            </AnimatePresence>
                            
                            {/* Navigation Arrows */}
                            <button
                                onClick={goToPrevious}
                                className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white backdrop-blur-sm p-2 md:p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                                aria-label="Previous image"
                            >
                                <ChevronLeft className="w-4 h-4 md:w-5 md:h-5 text-gray-800" />
                            </button>
                            <button
                                onClick={goToNext}
                                className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white backdrop-blur-sm p-2 md:p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                                aria-label="Next image"
                            >
                                <ChevronRight className="w-4 h-4 md:w-5 md:h-5 text-gray-800" />
                            </button>
                            
                            {/* Auto-play Toggle */}
                            <button
                                onClick={toggleAutoPlay}
                                className="absolute top-4 right-4 bg-white/90 hover:bg-white backdrop-blur-sm p-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                                aria-label={isAutoPlaying ? "Pause slideshow" : "Play slideshow"}
                            >
                                {isAutoPlaying ? (
                                    <Pause className="w-4 h-4 text-gray-800" />
                                ) : (
                                    <Play className="w-4 h-4 text-gray-800" />
                                )}
                            </button>
                        </div>
                        
                        {/* Image Dots Indicator */}
                        <div className="flex justify-center gap-1.5 mt-4">
                            {heroImages.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => goToImage(index)}
                                    className={`h-1.5 rounded-full transition-all duration-300 ${
                                        index === currentImageIndex 
                                            ? 'w-6 bg-blue-600' 
                                            : 'w-1.5 bg-gray-300 hover:bg-gray-400'
                                    }`}
                                    aria-label={`Go to image ${index + 1}`}
                                />
                            ))}
                        </div>
                        
                        {/* Image Counter */}
                        <div className="text-center mt-2 text-gray-600 text-xs md:text-sm">
                            <span className="font-medium">{currentImageIndex + 1}</span>
                            <span className="mx-1">/</span>
                            <span>{heroImages.length}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Scroll Indicator - Only on desktop */}
            <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ repeat: Infinity, duration: 2 }}
                className="absolute bottom-4 left-1/2 transform -translate-x-1/2 hidden lg:block"
            >
                <div className="flex flex-col items-center gap-1">
                    <span className="text-xs text-gray-500 font-medium">Scroll to explore</span>
                    <div className="text-blue-500">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                        </svg>
                    </div>
                </div>
            </motion.div>
        </section>
    );
};

export default HeroSection;